import { useEffect, useState } from 'react';
import type React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, FileQuestion, Receipt, PackageCheck,
  ClipboardList, ClipboardCheck, Truck, Palette, Upload, X, Code2, Quote, Clock, Send, Mail,
} from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { getSettings, getStageEmails, updateSettings } from '@/api/admin';
import { InternalEmailPanel, type StageTemplate } from './InternalEmailPanel';
import logoLight from '@/assets/logo-light.svg';
import { getSummaryEmailPreview, sendManagementSummary } from '@/api/managementSummary';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BrandTemplate {
  headerFrom: string;
  headerTo: string;
  footerFrom: string;
  footerTo: string;
  footerText: string;
  accentColor: string;
  customHtml?: string;  // full HTML email template (overrides color builder)
}

export interface DocTemplate {
  subject: string;
  emailBody: string;
  footerNotes: string;
}

export interface DocTemplatesMap {
  _brand: BrandTemplate;
  po: DocTemplate;
  rfq: DocTemplate;
  'sales-invoice': DocTemplate;
  quotation: DocTemplate;
  mrn: DocTemplate;
  pr: DocTemplate;
  grn: DocTemplate;
  'grn-out': DocTemplate;
  /** Internal (workflow handoff) emails by stage key; blank fields = default wording. */
  _internal?: Record<string, StageTemplate>;
}

// ─── Doc type metadata ────────────────────────────────────────────────────────
type DocKey = Exclude<keyof DocTemplatesMap, '_brand' | '_internal'>;

/** Shorter names for the Internal Emails list so each fits on one line. */
const SIDEBAR_LABEL: Record<string, string> = {
  'weight-hold': 'Weight Hold',
  mrn: 'Material Requisition',
  'grn-in': 'Goods Receipt (GRN)',
};

const DOC_TYPES: { key: DocKey; label: string; icon: React.ReactNode; vars: string[] }[] = [
  { key: 'po',            label: 'Purchase Order',        icon: <ShoppingCart size={15} />, vars: ['{{docNo}}','{{docDate}}','{{vendorName}}','{{totalAmount}}','{{company}}','{{companyEmail}}','{{companyPhone}}'] },
  { key: 'rfq',           label: 'Request for Quotation', icon: <FileQuestion size={15} />, vars: ['{{docNo}}','{{docDate}}','{{dueDate}}','{{vendorName}}','{{company}}','{{companyEmail}}'] },
  { key: 'quotation',     label: 'Customer Quotation',    icon: <Quote size={15} />,        vars: ['{{docNo}}','{{docDate}}','{{partyName}}','{{contactPerson}}','{{customerType}}','{{totalAmount}}','{{taxAmount}}','{{amountInWords}}','{{expectedDelivery}}','{{validityDays}}','{{company}}','{{companyEmail}}','{{companyPhone}}'] },
  { key: 'sales-invoice', label: 'Sales Invoice',         icon: <Receipt size={15} />,      vars: ['{{docNo}}','{{docDate}}','{{dueDate}}','{{partyName}}','{{totalAmount}}','{{taxAmount}}','{{amountInWords}}','{{company}}','{{companyEmail}}','{{companyPhone}}'] },
  { key: 'mrn',           label: 'Material Receipt Note', icon: <PackageCheck size={15} />, vars: ['{{docNo}}','{{docDate}}','{{vendorName}}','{{company}}'] },
  { key: 'pr',            label: 'Purchase Requisition',  icon: <ClipboardList size={15} />, vars: ['{{docNo}}','{{docDate}}','{{requestedBy}}','{{company}}'] },
  { key: 'grn',           label: 'Goods Receipt Note',    icon: <ClipboardCheck size={15} />, vars: ['{{docNo}}','{{docDate}}','{{vendorName}}','{{poNo}}','{{company}}'] },
  { key: 'grn-out',       label: 'Dispatch Note',         icon: <Truck size={15} />,        vars: ['{{docNo}}','{{docDate}}','{{partyName}}','{{orderNo}}','{{courier}}','{{awbNo}}','{{trackingLink}}','{{company}}','{{companyEmail}}','{{companyPhone}}'] },
];

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_BRAND: BrandTemplate = {
  headerFrom: '#06b6d4',
  headerTo: '#1d4ed8',
  footerFrom: '#06b6d4',
  footerTo: '#1d4ed8',
  footerText: 'This is an automated email. Please do not reply.',
  accentColor: '#dc2626',
};

const EMPTY_DOC: DocTemplate = { subject: '', emailBody: '', footerNotes: '' };

function defaultTemplates(): DocTemplatesMap {
  return {
    _brand: { ...DEFAULT_BRAND },
    po: { ...EMPTY_DOC },
    rfq: { ...EMPTY_DOC },
    quotation: {
      subject: 'Quotation {{docNo}} from {{company}}',
      emailBody: 'Dear {{partyName}},\n\nThank you for your enquiry. Please find attached our quotation {{docNo}} dated {{docDate}} for the items you requested.\n\nGrand Total: {{totalAmount}} ({{amountInWords}})\nValid for: {{validityDays}} days\n\nDo let us know if you have any questions — we\'d be happy to help.\n\nWarm regards,\n{{company}}',
      footerNotes: 'This quotation is valid for 15 days from the issue date. Prices are subject to change without notice.',
    },
    'sales-invoice': { ...EMPTY_DOC },
    mrn: { ...EMPTY_DOC },
    pr: { ...EMPTY_DOC },
    grn: { ...EMPTY_DOC },
    'grn-out': {
      subject: 'Your order {{orderNo}} has been dispatched — {{company}}',
      emailBody: 'Dear {{partyName}},\n\nGood news — your order {{orderNo}} has been packed and dispatched on {{docDate}}. The dispatch note is attached.\n\nCourier: {{courier}}\nAWB / Tracking No: {{awbNo}}\nTrack your parcel: {{trackingLink}}\n\nFor any help, write to {{companyEmail}} or call {{companyPhone}}.\n\nWarm regards,\n{{company}}',
      footerNotes: '',
    },
  };
}

function parseTemplates(raw: string | null): DocTemplatesMap {
  if (!raw) return defaultTemplates();
  try {
    const parsed = JSON.parse(raw);
    const defaults = defaultTemplates();
    if (parsed._brand) defaults._brand = { ...DEFAULT_BRAND, ...parsed._brand };
    if (parsed._internal && typeof parsed._internal === 'object') defaults._internal = parsed._internal;
    for (const d of DOC_TYPES) {
      if (parsed[d.key]) defaults[d.key] = { ...EMPTY_DOC, ...parsed[d.key] };
    }
    return defaults;
  } catch {
    return defaultTemplates();
  }
}

// ─── Email preview ────────────────────────────────────────────────────────────

const HEADER_SHORT: Record<string, string> = {
  'Material Receipt Note': 'MRN',
  'Goods Receipt Note': 'GRN',
  'Purchase Requisition': 'PR',
  'Request for Quotation': 'RFQ',
};

function EmailPreview({
  brand, docLabel, body, footerNotes, vars,
}: {
  brand: BrandTemplate; docLabel: string; body: string; footerNotes: string; vars: string[];
}) {
  // If a custom HTML template is uploaded, render it in an iframe
  if (brand.customHtml) {
    return (
      <div className="overflow-hidden rounded-xl border border-[var(--border-soft)] shadow-sm">
        <div className="flex items-center gap-1.5 bg-[var(--bg-soft)] px-3 py-1.5">
          <Code2 size={11} className="text-accent" />
          <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">Custom HTML Template</span>
        </div>
        <iframe
          srcDoc={brand.customHtml}
          title="Custom email template preview"
          className="h-96 w-full border-0"
          sandbox="allow-same-origin"
        />
      </div>
    );
  }

  // Long names use their short code in the header, as the sent email does.
  const headerLabel = HEADER_SHORT[docLabel] ?? docLabel;
  const previewBody = body.trim() || `Dear {{partyName}},\n\nPlease find attached the ${docLabel}.\n\nWe appreciate your business!`;
  const previewFooter = footerNotes.trim() || 'Thank you for your business.';
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--border-soft)] shadow-sm text-[11px]">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-4"
        style={{ background: `linear-gradient(135deg, ${brand.headerFrom}, ${brand.headerTo})` }}
      >
        <img src={logoLight} alt="GRHAMOY" className="h-14 w-auto" />
        <div className="text-lg font-black uppercase tracking-widest text-white opacity-90">
          {headerLabel}
        </div>
      </div>
      {/* Accent divider */}
      <div className="h-0.5" style={{ background: brand.accentColor }} />
      {/* Body */}
      <div className="bg-white px-4 py-4 text-gray-700">
        <p className="mb-3 whitespace-pre-line leading-relaxed text-xs">{previewBody}</p>
        <div className="my-3 space-y-1 rounded border border-gray-100 bg-gray-50 p-2.5">
          {vars.slice(0, 5).map((v) => (
            <div key={v} className="flex gap-2">
              <span className="w-28 shrink-0 text-[10px] font-semibold text-gray-500">
                {v.replace(/\{\{|\}\}/g, '').replace(/([A-Z])/g, ' $1').trim()}:
              </span>
              <span className="font-mono text-[10px] text-gray-400">{v}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[10px] text-gray-500">{previewFooter}</p>
      </div>
      {/* Footer */}
      <div
        className="px-4 py-3 text-center"
        style={{ background: `linear-gradient(135deg, ${brand.footerFrom}, ${brand.footerTo})` }}
      >
        <p className="text-[10px] text-white/80">{'© {{company}} {{companyAddress}}'}</p>
        <p className="mt-0.5 text-[10px] text-white/60">{brand.footerText}</p>
      </div>
    </div>
  );
}

// ─── Color row helper ─────────────────────────────────────────────────────────

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 cursor-pointer rounded border border-[var(--border-soft)] bg-transparent p-0.5"
      />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="w-28 font-mono text-xs" placeholder="#000000" />
      <span className="text-xs text-[var(--text-muted)]">{label}</span>
    </div>
  );
}

// ─── Variable chip ────────────────────────────────────────────────────────────

function VarChip({ v, onInsert }: { v: string; onInsert: (v: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onInsert(v)}
      className="rounded border border-[var(--border-soft)] bg-[var(--bg-soft)] px-2 py-0.5 font-mono text-[10px] text-[var(--text-secondary)] transition-colors hover:border-accent hover:text-accent"
      title={`Insert ${v}`}
    >
      {v}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type ActiveTab = '_brand' | '_mgmt' | `int:${string}` | DocKey;

export function DocTemplatesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings });
  const { data: stageEmails = [] } = useQuery({ queryKey: ['stage-emails'], queryFn: getStageEmails });
  const [templates, setTemplates] = useState<DocTemplatesMap>(defaultTemplates());
  const [activeTab, setActiveTab] = useState<ActiveTab>('_brand');
  const [focusedField, setFocusedField] = useState<keyof DocTemplate>('emailBody');
  const [brandMode, setBrandMode] = useState<'builder' | 'custom'>('builder');
  const fileInputRef = useState<HTMLInputElement | null>(null);

  useEffect(() => {
    if (settings) setTemplates(parseTemplates(settings.docTemplates));
  }, [settings]);

  const mut = useMutation({
    mutationFn: (map: DocTemplatesMap) => updateSettings({ docTemplates: JSON.stringify(map) }),
    onSuccess: () => {
      toast.push('Templates saved', 'success');
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  function setBrand(field: keyof BrandTemplate, value: string) {
    setTemplates((prev) => ({ ...prev, _brand: { ...prev._brand, [field]: value } }));
  }

  function handleHtmlUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.html') && file.type !== 'text/html') {
      toast.push('Please upload an .html file', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const html = ev.target?.result as string;
      setBrand('customHtml', html);
      setBrandMode('custom');
      toast.push(`Template "${file.name}" loaded — save to apply`, 'success');
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function clearCustomHtml() {
    setTemplates((prev) => {
      const { customHtml: _, ...rest } = prev._brand;
      return { ...prev, _brand: rest as BrandTemplate };
    });
    setBrandMode('builder');
  }

  function setDocField(key: DocKey, field: keyof DocTemplate, value: string) {
    setTemplates((prev) => ({ ...prev, [key]: { ...(prev[key] as DocTemplate), [field]: value } }));
  }

  function insertVar(v: string) {
    if (activeTab === '_brand') return;
    const key = activeTab as DocKey;
    const cur = templates[key] as DocTemplate;
    setDocField(key, focusedField, cur[focusedField] + v);
  }

  const brand = templates._brand;
  const activeDocType = DOC_TYPES.find((d) => d.key === activeTab);
  const activeDocTemplate = activeTab !== '_brand'
    ? (templates[activeTab as DocKey] as DocTemplate)
    : null;

  return (
    <div>
      <SectionHeader
        title="Email Templates"        actions={
          <div className="flex gap-2">
            <Button variant="glass" onClick={() => navigate('/admin/configurations')}>← Back</Button>
            <Button variant="primary" onClick={() => mut.mutate(templates)} disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Save Templates'}
            </Button>
          </div>
        }
      />

      <div data-compact-form="true" className="mt-4 flex gap-4">
        {/* ── Sidebar ── */}
        <div className="flex w-52 shrink-0 flex-col gap-0.5">
          <button
            onClick={() => setActiveTab('_brand')}
            className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-all ${
              activeTab === '_brand' ? 'bg-accent text-white shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-soft)]'
            }`}
          >
            <Palette size={15} />
            <span>Brand Template</span>
          </button>

          <div className="my-1.5 border-t border-[var(--border-soft)]" />
          <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
            Document Types
          </p>

          {DOC_TYPES.map((doc) => (
            <button
              key={doc.key}
              onClick={() => setActiveTab(doc.key)}
              className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-all ${
                activeTab === doc.key ? 'bg-accent text-white shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-soft)]'
              }`}
            >
              <span>{doc.icon}</span>
              <span className="leading-tight">{doc.label}</span>
            </button>
          ))}

          <div className="my-1.5 border-t border-[var(--border-soft)]" />
          <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
            Internal Emails
          </p>
          {stageEmails.map((st) => (
            <button
              key={st.key}
              onClick={() => setActiveTab(`int:${st.key}`)}
              title={`${st.label} ${st.action}`}
              className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-all ${
                activeTab === `int:${st.key}` ? 'bg-accent text-white shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-soft)]'
              }`}
            >
              <span><Mail size={15} /></span>
              <span className="min-w-0 flex-1 leading-tight">{SIDEBAR_LABEL[st.key] ?? st.label}</span>
              {!st.enabled && (
                <span className={`shrink-0 rounded px-1.5 py-px text-[9px] font-bold uppercase ${activeTab === `int:${st.key}` ? 'bg-white/25 text-white' : 'bg-[var(--bg-soft)] text-[var(--text-muted)]'}`}>
                  Off
                </span>
              )}
            </button>
          ))}

          <div className="my-1.5 border-t border-[var(--border-soft)]" />
          <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
            Scheduled Emails
          </p>
          <button
            onClick={() => setActiveTab('_mgmt')}
            className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-all ${
              activeTab === '_mgmt' ? 'bg-accent text-white shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-soft)]'
            }`}
          >
            <span><Clock size={15} /></span>
            <span className="leading-tight">Daily Management Summary</span>
          </button>
        </div>

        {activeTab === '_mgmt' ? <MorningSummaryPanel /> : activeTab.startsWith('int:') ? (() => {
          const key = activeTab.slice(4);
          const info = stageEmails.find((x) => x.key === key);
          return info ? (
            <InternalEmailPanel
              key={key}
              info={info}
              value={templates._internal?.[key]}
              onChange={(t) => setTemplates((prev) => ({ ...prev, _internal: { ...prev._internal, [key]: t } }))}
              onSave={() => mut.mutate(templates)}
              saving={mut.isPending}
            />
          ) : null;
        })() : (
        /* ── Editor + Preview ── */
        <div className="flex flex-1 gap-4">

          {/* Editor panel */}
          <div className="flex-1 rounded-xl border border-[var(--border-soft)] bg-[var(--bg-card)] p-5">

            {/* Brand Template editor */}
            {activeTab === '_brand' && (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Palette size={16} className="text-accent" />
                    <h3 className="text-base font-extrabold text-[var(--text-primary)]">Brand Template</h3>
                  </div>
                  {/* Mode toggle */}
                  <div className="flex rounded-lg border border-[var(--border-soft)] overflow-hidden text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setBrandMode('builder')}
                      className={`px-3 py-1.5 transition-colors ${
                        brandMode === 'builder'
                          ? 'bg-accent text-white'
                          : 'bg-[var(--bg-soft)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      Color Builder
                    </button>
                    <button
                      type="button"
                      onClick={() => setBrandMode('custom')}
                      className={`px-3 py-1.5 transition-colors ${
                        brandMode === 'custom'
                          ? 'bg-accent text-white'
                          : 'bg-[var(--bg-soft)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      Custom HTML
                    </button>
                  </div>
                </div>
                <p className="mb-5 text-xs text-[var(--text-muted)]">
                  {brandMode === 'builder'
                    ? 'Set the global visual style applied to all outgoing document emails. The preview updates live.'
                    : 'Upload your own HTML email template file. Use {{variables}} as placeholders for dynamic content.'}
                </p>

                {/* Color builder mode */}
                {brandMode === 'builder' && (
                <div className="grid grid-cols-1 gap-5">
                  <section>
                    <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Header Gradient</h4>
                    <div className="space-y-2">
                      <ColorRow label="Start color" value={brand.headerFrom} onChange={(v) => setBrand('headerFrom', v)} />
                      <ColorRow label="End color"   value={brand.headerTo}   onChange={(v) => setBrand('headerTo', v)} />
                    </div>
                    <div className="mt-2 h-5 rounded-md" style={{ background: `linear-gradient(90deg, ${brand.headerFrom}, ${brand.headerTo})` }} />
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Footer Gradient</h4>
                    <div className="space-y-2">
                      <ColorRow label="Start color" value={brand.footerFrom} onChange={(v) => setBrand('footerFrom', v)} />
                      <ColorRow label="End color"   value={brand.footerTo}   onChange={(v) => setBrand('footerTo', v)} />
                    </div>
                    <div className="mt-2 h-5 rounded-md" style={{ background: `linear-gradient(90deg, ${brand.footerFrom}, ${brand.footerTo})` }} />
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Accent Color (divider line)</h4>
                    <ColorRow label="Accent" value={brand.accentColor} onChange={(v) => setBrand('accentColor', v)} />
                    <div className="mt-2 h-1.5 rounded-full" style={{ background: brand.accentColor }} />
                  </section>

                  <section>
                    <FormGroup label="Footer disclaimer text">
                      <Input
                        value={brand.footerText}
                        onChange={(e) => setBrand('footerText', e.target.value)}
                        placeholder="This is an automated email. Please do not reply."
                      />
                    </FormGroup>
                  </section>
                </div>
                )}

                {/* Custom HTML upload mode */}
                {brandMode === 'custom' && (
                <div className="grid grid-cols-1 gap-5">
                  {/* Upload area */}
                  <section>
                    <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Upload HTML Template</h4>
                    <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[var(--border-soft)] bg-[var(--bg-soft)] py-8 transition-colors hover:border-accent hover:bg-accent/5">
                      <Upload size={22} className="text-[var(--text-muted)]" />
                      <span className="text-sm font-semibold text-[var(--text-secondary)]">Click to upload .html file</span>
                      <span className="text-xs text-[var(--text-muted)]">Max 500 KB · Use {'{{variables}}'} for dynamic content</span>
                      <input
                        type="file"
                        accept=".html,text/html"
                        className="hidden"
                        onChange={handleHtmlUpload}
                        ref={(el) => { fileInputRef[1](el); }}
                      />
                    </label>
                  </section>

                  {/* Current template status */}
                  {brand.customHtml ? (
                    <section className="rounded-lg border border-green-400/30 bg-green-400/10 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Code2 size={14} className="text-green-600" />
                          <span className="text-xs font-bold text-green-700">Custom HTML template active</span>
                        </div>
                        <button
                          type="button"
                          onClick={clearCustomHtml}
                          className="flex items-center gap-1 rounded-md border border-danger/30 bg-danger/10 px-2 py-1 text-[10px] font-bold text-danger hover:bg-danger/20 transition-colors"
                        >
                          <X size={11} /> Remove &amp; use builder
                        </button>
                      </div>
                      <p className="mt-1.5 text-[10px] text-green-600/80">
                        {brand.customHtml.length.toLocaleString()} characters loaded — preview shown on the right
                      </p>
                    </section>
                  ) : (
                    <section className="rounded-lg border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3">
                      <p className="text-xs text-[var(--text-muted)]">No custom HTML uploaded yet. Upload a file above or switch to Color Builder.</p>
                    </section>
                  )}

                  {/* Variable reference */}
                  <section>
                    <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Supported Variables (use in your HTML)</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {['{{company}}','{{companyAddress}}','{{companyEmail}}','{{companyPhone}}','{{docNo}}','{{docDate}}','{{dueDate}}','{{partyName}}','{{totalAmount}}','{{taxAmount}}','{{amountInWords}}'].map((v) => (
                        <span key={v} className="rounded border border-[var(--border-soft)] bg-[var(--bg-card)] px-2 py-0.5 font-mono text-[10px] text-[var(--text-secondary)]">{v}</span>
                      ))}
                    </div>
                  </section>
                </div>
                )}
              </>
            )}

            {/* Doc type editor */}
            {activeTab !== '_brand' && activeDocType && activeDocTemplate && (
              <>
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-[var(--text-muted)]">{activeDocType.icon}</span>
                  <h3 className="text-base font-extrabold text-[var(--text-primary)]">{activeDocType.label}</h3>
                </div>

                {/* Variable chips */}
                <div className="mb-4 rounded-lg border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                    Available variables — click to insert into the focused field
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {activeDocType.vars.map((v) => (
                      <VarChip key={v} v={v} onInsert={insertVar} />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <FormGroup label="Email Subject" hint="Leave blank to use system default.">
                    <Input
                      value={activeDocTemplate.subject}
                      onFocus={() => setFocusedField('subject')}
                      onChange={(e) => setDocField(activeTab as DocKey, 'subject', e.target.value)}
                      placeholder={`e.g. ${activeDocType.label} — {{docNo}} from {{company}}`}
                    />
                  </FormGroup>

                  <FormGroup label="Email Body" hint="Leave blank to use system default.">
                    <Textarea
                      rows={6}
                      value={activeDocTemplate.emailBody}
                      onFocus={() => setFocusedField('emailBody')}
                      onChange={(e) => setDocField(activeTab as DocKey, 'emailBody', e.target.value)}
                      placeholder={`Dear {{partyName}},\n\nPlease find attached the ${activeDocType.label}.\n\nRegards,\n{{company}}`}
                    />
                  </FormGroup>

                  <FormGroup label="Footer Notes" hint="Printed at the bottom of the document. Overrides global T&C for this doc type.">
                    <Textarea
                      rows={3}
                      value={activeDocTemplate.footerNotes}
                      onFocus={() => setFocusedField('footerNotes')}
                      onChange={(e) => setDocField(activeTab as DocKey, 'footerNotes', e.target.value)}
                      placeholder="e.g. Goods once sold will not be taken back. Subject to local jurisdiction."
                    />
                  </FormGroup>
                </div>
              </>
            )}

            <div className="mt-5 flex justify-end">
              <Button variant="primary" onClick={() => mut.mutate(templates)} disabled={mut.isPending}>
                {mut.isPending ? 'Saving…' : 'Save Templates'}
              </Button>
            </div>
          </div>

          {/* Live preview */}
          <div className="w-72 shrink-0">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
              Live Email Preview
            </p>
            <EmailPreview
              brand={brand}
              docLabel={activeDocType?.label ?? 'Document'}
              body={activeDocTemplate?.emailBody ?? ''}
              footerNotes={activeDocTemplate?.footerNotes ?? ''}
              vars={activeDocType?.vars ?? []}
            />
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

// ─── Daily Management Summary (8 AM email) ───────────────────────────────────

/** The morning email is built from live ERP figures, so it is shown here as sent — not edited. */
function MorningSummaryPanel() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['mgmt-summary-email'], queryFn: getSummaryEmailPreview });
  const [frameH, setFrameH] = useState(900);
  const toast = useToast();
  const sendMut = useMutation({
    mutationFn: sendManagementSummary,
    onSuccess: (r) => {
      toast.push(`Sent — ${r.emailed} email(s), ${r.whatsapped} WhatsApp(s) to ${r.recipients} recipient(s)`, r.errors.length ? 'warning' : 'success');
      if (r.errors.length) toast.push(r.errors.slice(0, 2).join(' · '), 'warning');
    },
    onError: (e) => toast.push(apiError(e, 'Send failed'), 'error'),
  });
  const d = q.data;
  return (
    <div className="min-w-0 flex-1 rounded-xl border border-[var(--border-soft)] bg-[var(--bg-card)] p-5">
      <div className="mb-1 flex items-center gap-2">
        <Clock size={15} className="text-accent" />
        <h3 className="text-base font-bold text-[var(--text-primary)]">Daily Management Summary</h3>
      </div>
      <p className="mb-4 text-xs text-[var(--text-muted)]">
        Goes out at 8 AM every day (switch it on or off in Email settings) — the full report by email, a headline and link by WhatsApp. Its figures come
        live from the ERP, so there is no text to edit; the preview below is today's email exactly as sent.
      </p>

      {q.isLoading && <div className="py-8 text-center text-sm text-[var(--text-muted)]">Preparing today's email…</div>}
      {q.isError && <div className="py-8 text-center text-sm text-danger">{apiError(q.error, 'Could not load the email')}</div>}

      {d && (
        <>
          <div className="mb-4 space-y-3 rounded-lg border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3.5 text-xs">
            <div>
              <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Subject</div>
              <div className="font-semibold text-[var(--text-primary)]">{d.subject}</div>
            </div>
            <div>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
                Recipients ({d.recipients.length}) — everyone holding the ‘Daily Summary’ permission
              </div>
              <div className="flex flex-wrap gap-1.5">
                {d.recipients.length === 0 && <span className="text-[var(--text-muted)]">Nobody yet — grant it in Role / User Management.</span>}
                {d.recipients.map((r) => (
                  <span
                    key={r.fullName + (r.email ?? '')}
                    title={r.email ?? 'No email on file — WhatsApp only'}
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.email ? 'bg-accent/15 text-accent' : 'bg-[var(--bg-card)] text-[var(--text-muted)]'}`}
                  >
                    {r.fullName}{r.email ? '' : ' · no email'}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="glass" onClick={() => navigate('/admin/configurations/email')}>8 AM on / off — Email settings</Button>
              <Button size="sm" variant="primary" icon={<Send size={12} />} onClick={() => sendMut.mutate()} disabled={sendMut.isPending}>
                {sendMut.isPending ? 'Sending…' : 'Send Now (Email + WhatsApp)'}
              </Button>
            </div>
          </div>

          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Email Preview</p>
          <iframe
            title="Daily Management Summary email"
            srcDoc={`<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:16px;background:#f5f5f5">${d.html}</body></html>`}
            sandbox="allow-same-origin"
            className="w-full rounded-xl border border-[var(--border-soft)] bg-white"
            style={{ height: frameH }}
            onLoad={(e) => {
              const h = e.currentTarget.contentDocument?.documentElement.scrollHeight;
              if (h) setFrameH(h + 4);
            }}
          />
        </>
      )}
    </div>
  );
}
